import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Copy, Check, Link as LinkIcon, Target, Send, Wallet } from 'lucide-react';
import PartnerLeads from './Leads';

/**
 * Invite — the one place a partner brings someone in, and the one place that says what each
 * way MEANS.
 *
 * Why this page exists. There are four arrangements and each pays differently (solution 15%,
 * referral 10%, affiliate a flat fee, reseller nothing — the margin was taken at purchase).
 * The three doors into them used to be three unrelated screens: the lead form here, the
 * referral link buried in Profile, the reseller invite inside Client Credits. None of them
 * said which arrangement they produced, so a partner could not tell how a client they brought
 * in would be attributed — and two of the four arrangements could not be reached at all.
 *
 * So each tab states its arrangement plainly. Two of the three are fixed by what the door IS:
 * a referral-link signup is a referral client by definition, and a reseller invite means the
 * partner bills them. Only Add a lead has a choice, and that choice is a REQUEST — Expedify
 * confirms it, because a partner who could set their own rate would set it to the best one.
 */

const EXPEDIFY_APP_URL = process.env.REACT_APP_EXPEDIFY_APP_URL || 'https://app.expedify.ai';

const TABS = [
  {
    id: 'lead',
    label: 'Add a lead',
    icon: Target,
    blurb: 'You make the introduction. We sell, onboard and bill them.',
    arrangement: 'You choose — referral, solution or affiliate',
  },
  {
    id: 'referral',
    label: 'Referral link',
    icon: LinkIcon,
    blurb: 'They sign themselves up. No form for you to fill in, no lead to type.',
    arrangement: 'Always referral',
  },
  {
    id: 'reseller',
    label: 'Reseller invite',
    icon: Wallet,
    blurb: 'You buy credits, you bill the client, you keep the margin.',
    arrangement: 'Always reseller — no commission, the margin was taken at purchase',
  },
];

const PartnerInvite = () => {
  const { user, getAuthHeaders, API_URL } = useAuth();
  const [tab, setTab] = useState('lead');
  const [copied, setCopied] = useState('');

  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [minting, setMinting] = useState(false);
  // Only for the cost disclosure in the invite dialog below. A reseller invite spends
  // the partner's own credits the moment the client signs up, so the number has to be
  // on screen BEFORE the button, not discovered on the balance afterwards.
  const [balance, setBalance] = useState(null);

  const referralLink = `${EXPEDIFY_APP_URL}/signup?ref=${user?.referral_code || ''}`;

  const loadInvites = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/partner/client-invites`, {
        headers: getAuthHeaders(),
      });
      setInvites(res.data.invites || []);
    } catch {
      /* non-blocking: the tab still explains itself without the list */
    }
    try {
      const bal = await axios.get(`${API_URL}/api/partner/credits`, {
        headers: getAuthHeaders(),
      });
      setBalance(bal.data);
    } catch {
      /* non-blocking: the dialog falls back to naming no figures rather than wrong ones */
    }
  }, [API_URL, getAuthHeaders]);

  const formatCredits = (n) =>
    new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n || 0);

  useEffect(() => {
    if (tab === 'reseller') loadInvites();
  }, [tab, loadInvites]);

  const copy = (value, key, message) => {
    if (!value) {
      toast.error('Nothing to copy yet.');
      return;
    }
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(''), 1800);
    toast.success(message);
  };

  const mintInvite = async () => {
    setMinting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/partner/client-invites`,
        { client_email: inviteEmail || '', client_name: inviteName || '' },
        { headers: getAuthHeaders() }
      );
      copy(res.data.signup_url, 'minted', 'Invite link created and copied.');
      setInviteOpen(false);
      setInviteEmail('');
      setInviteName('');
      loadInvites();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail?.message || detail || 'Could not create an invite link');
    } finally {
      setMinting(false);
    }
  };

  const pendingInvites = invites.filter((i) => i.status === 'pending');
  const active = TABS.find((t) => t.id === tab);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="partner-invite">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Invite</h1>
          <p className="text-sm text-[#64748B] mt-1">
            Every way to bring someone in, and what each one means for how you are paid.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = tab === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setTab(t.id)}
                data-testid={`invite-tab-${t.id}`}
                className={`flex items-center gap-2 rounded-lg border px-4 h-10 text-sm transition-colors ${
                  isActive
                    ? 'border-[#ef785b] bg-[#ef785b]/5 text-[#1A1A1A] font-medium'
                    : 'border-[#E2E2DF] bg-white text-[#64748B] hover:border-[#cfcfca]'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* What this door means — the thing that was missing everywhere */}
        {active && (
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <p className="text-sm text-[#1A1A1A]">{active.blurb}</p>
              <p className="text-xs text-[#64748B] mt-1">
                <span className="font-semibold">Arrangement:</span> {active.arrangement}
              </p>
            </CardContent>
          </Card>
        )}

        {tab === 'lead' && <PartnerLeads embedded />}

        {tab === 'referral' && (
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4 space-y-4">
              <div>
                <Label className="text-sm font-medium text-[#1A1A1A]">Your referral link</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    value={referralLink}
                    readOnly
                    className="h-10 border-[#E2E2DF] bg-[#FAFAF8]"
                    data-testid="referral-link"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 border-[#E2E2DF]"
                    onClick={() => copy(referralLink, 'link', 'Referral link copied.')}
                    data-testid="copy-referral-link"
                  >
                    {copied === 'link' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-[#64748B] mt-1.5">
                  Anyone who signs up through this is attributed to you automatically. You do
                  not need to add them as a lead as well.
                </p>
              </div>

              <div className="pt-2 border-t border-[#E2E2DF]">
                <Label className="text-sm font-medium text-[#1A1A1A]">Your code</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    value={(user?.referral_code || '').toUpperCase()}
                    readOnly
                    className="h-10 border-[#E2E2DF] bg-[#FAFAF8] font-semibold tracking-wider"
                    data-testid="promo-code"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 border-[#E2E2DF]"
                    onClick={() =>
                      copy((user?.referral_code || '').toUpperCase(), 'code', 'Code copied.')
                    }
                    data-testid="copy-promo-code"
                  >
                    {copied === 'code' ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-[#64748B] mt-1.5">
                  The same code, for when someone is typing it rather than clicking a link.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {tab === 'reseller' && (
          <Card className="bg-white border-[#E2E2DF]">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <p className="text-sm text-[#64748B]">
                  Send them a link. They sign up themselves and land on your credits — an
                  account made <em>for</em> them has an owner who has never logged into it.
                </p>
                <Button
                  className="bg-[#ef785b] hover:bg-[#e56847] text-white flex-shrink-0"
                  onClick={() => setInviteOpen(true)}
                  data-testid="new-client-invite"
                >
                  <Send className="w-4 h-4 mr-2" /> Create invite link
                </Button>
              </div>

              {pendingInvites.length > 0 && (
                <div className="mt-4 border-t border-[#E2E2DF] pt-3 space-y-2">
                  {pendingInvites.map((invite) => (
                    <div
                      key={invite.id}
                      className="flex flex-wrap items-center justify-between gap-2 text-xs"
                    >
                      <span className="text-[#64748B]">
                        <span className="font-medium text-[#1A1A1A]">
                          {invite.client_name || invite.client_email || 'Unnamed client'}
                        </span>
                        {' · '}awaiting signup
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-[#E2E2DF] h-7 text-xs"
                        onClick={() => copy(invite.signup_url, invite.id, 'Invite link copied.')}
                      >
                        Copy link
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs text-[#64748B] mt-4">
                Cancelling an invite, and topping up a client, both live on{' '}
                <a href="/credits" className="underline">Client Credits</a>.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Create an invite link</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Client name</Label>
              <Input
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Optional — so you can tell your invites apart"
                className="mt-1.5 h-10 border-[#E2E2DF]"
              />
            </div>
            <div>
              <Label className="text-sm">Client email</Label>
              <Input
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="Optional"
                className="mt-1.5 h-10 border-[#E2E2DF]"
              />
            </div>
            {/* Said plainly, before the click. The seed is charged when the client signs up,
                not now — a partner should never discover a charge afterwards. This moved here
                from Client Credits, which used to carry a second copy of this whole flow. */}
            {balance && (
              <div className="rounded-lg bg-[#FAFAF8] p-3 text-xs text-[#64748B]">
                <div className="flex justify-between">
                  <span>They start with</span>
                  <span className="font-medium text-[#1A1A1A]">
                    {formatCredits(balance.min_client_seed)} credits
                  </span>
                </div>
                <div className="flex justify-between mt-1">
                  <span>Taken from your balance when they sign up</span>
                  <span className="font-medium text-[#1A1A1A]">
                    {formatCredits(balance.credits_available)} available
                  </span>
                </div>
                <p className="mt-2">
                  The link works once, and expires in 30 days. Nothing leaves your balance
                  until they actually sign up.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button
              className="bg-[#ef785b] hover:bg-[#e56847] text-white"
              onClick={mintInvite}
              disabled={minting}
            >
              {minting ? 'Creating…' : 'Create and copy link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default PartnerInvite;
